/* eslint-disable no-unused-vars */
import { createMiddleware } from 'redux-beacon';
import combineEvents from '@redux-beacon/combine-events';

import { isEmployeeRate } from 'BookingFlow/utils';

import {
	addPackage,
	addPackageCancel,
	addPackageFailed,
	addPackageFulfilled,
	cancelBooking,
	cancelBookingCancel,
	cancelBookingFailed,
	cancelBookingFulfilled,
	completeBooking,
	completeBookingCancel,
	completeBookingFailed,
	completeBookingFulfilled,
	submitBookingPreferences,
	submitBookingPreferencesCancel,
	submitBookingPreferencesFailed,
	submitBookingPreferencesFulfilled,
} from 'store/bookings';

import {
	apmTarget,
	apmCaptureError,
	customTransaction,
	endTransaction,
	apmAddLabels,
} from 'config/elasticApm';
import {
	fetchSearchResults,
	fetchSearchResultsFailed,
	fetchSearchResultsForResultSetIdFulfilled,
	fetchSearchResultsFulfilled,
} from './searchResults';
import {
	generateOtp,
	generateOtpCancel,
	generateOtpFailed,
	generateOtpFulfilled,
	submitOtp,
	submitOtpCancel,
	submitOtpFailed,
	submitOtpFulfilled,
} from './signIn';

function getUserType(searchParams) {
	return searchParams?.ppMode ? 'preferred' : isEmployeeRate(searchParams) ? 'employee' : 'guest';
}

function getErrorType({ errors, supplierErrors, apiErrors }) {
	if (supplierErrors?.[0]?.errorCode === 'E0301' || apiErrors?.[0]?.errorCode === 'E0301') {
		return 'no_availability';
	}
	if (supplierErrors?.[0]) {
		return 'supplier';
	}
	if (apiErrors?.[0] || errors?.[0]?.statusCode === 400) {
		return 'validation';
	}
	return 'unknown';
}

const eventsMap = {
	[generateOtp.type]: customTransaction('Generate OTP'),
	[generateOtpCancel.type]: endTransaction('Generate OTP'),
	[generateOtpFailed.type]: endTransaction('Generate OTP', 'failure'),
	[generateOtpFulfilled.type]: endTransaction('Generate OTP', 'success'),

	[submitOtp.type]: customTransaction('Submit OTP'),
	[submitOtpCancel.type]: endTransaction('Submit OTP'),
	[submitOtpFailed.type]: endTransaction('Submit OTP', 'failure'),
	[submitOtpFulfilled.type]: endTransaction('Submit OTP', 'success'),

	[fetchSearchResults.type]: combineEvents(
		customTransaction('Fetch Availability'),
		apmAddLabels('Fetch Availability', ({ payload: { searchParams } }) => ({
			type: getUserType(searchParams),
		}))
	),
	[fetchSearchResultsFailed.type]: combineEvents(
		apmAddLabels('Fetch Availability', ({ payload: { errors, supplierErrors, apiErrors } }) => {
			const errorType = getErrorType({ errors, supplierErrors, apiErrors });

			return {
				errorType,
				outcome:
					errorType === 'no_availability' || errorType === 'validation' ? 'success' : 'failure',
				errorCode:
					supplierErrors?.[0]?.errorCode ||
					apiErrors?.[0]?.errorCode ||
					errors?.[0]?.errorCode ||
					(errors?.[0]?.statusCode ? `HTTP ${errors?.[0]?.statusCode}` : ''),
				error:
					supplierErrors?.[0]?.errorMessage ||
					apiErrors?.[0]?.userMessage ||
					apiErrors?.[0]?.devMessage ||
					errors?.[0]?.message ||
					'Unexpected Error',
			};
		}),
		endTransaction('Fetch Availability')
	),
	[fetchSearchResultsFulfilled.type]: endTransaction('Fetch Availability', 'success'),
	[fetchSearchResultsForResultSetIdFulfilled.type]: endTransaction('Fetch Availability', 'success'),

	[addPackage.type]: customTransaction('Choose Your Room'),
	[addPackageCancel.type]: endTransaction('Choose Your Room'),
	[addPackageFailed.type]: endTransaction('Choose Your Room', 'failure'),
	[addPackageFulfilled.type]: endTransaction('Choose Your Room', 'success'),

	[submitBookingPreferences.type]: customTransaction('Personalize Booking'),
	[submitBookingPreferencesCancel.type]: endTransaction('Personalize Booking'),
	[submitBookingPreferencesFailed.type]: endTransaction('Personalize Booking', 'failure'),
	[submitBookingPreferencesFulfilled.type]: endTransaction('Personalize Booking', 'success'),

	[cancelBooking.type]: customTransaction('Cancel Booking'),
	[cancelBookingCancel.type]: endTransaction('Cancel Booking'),
	[cancelBookingFailed.type]: endTransaction('Cancel Booking', 'failure'),
	[cancelBookingFulfilled.type]: endTransaction('Cancel Booking', 'success'),

	[completeBooking.type]: combineEvents(
		customTransaction('Checkout'),
		apmAddLabels('Checkout', ({ payload: { searchParams } }) => ({
			type: getUserType(searchParams),
		}))
	),
	[completeBookingCancel.type]: endTransaction('Checkout'),
	[completeBookingFulfilled.type]: endTransaction('Checkout', 'success'),
	[completeBookingFailed.type]: combineEvents(
		apmAddLabels('Checkout', ({ payload: { errors, supplierErrors, apiErrors } }) => {
			const errorType = getErrorType({ errors, supplierErrors, apiErrors });

			return {
				errorType,
				outcome: errorType === 'validation' ? 'success' : 'failure',
				errorCode:
					supplierErrors?.[0]?.errorCode ||
					apiErrors?.[0]?.errorCode ||
					errors?.[0]?.errorCode ||
					(errors?.[0]?.statusCode ? `HTTP ${errors?.[0]?.statusCode}` : ''),
				error:
					supplierErrors?.[0]?.errorMessage ||
					apiErrors?.[0]?.userMessage ||
					apiErrors?.[0]?.devMessage ||
					errors?.[0]?.message ||
					'Unexpected Error',
			};
		}),
		apmCaptureError(({ payload: { errorContext } }) => ({
			error: 'makeReservationFailed',
			errorContext,
		})),
		endTransaction('Checkout')
	),
};

export default createMiddleware(eventsMap, apmTarget());
